import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "../auth/AuthContext";


const WEBHOOK_URL = "https://n8n.easivio.io/webhook-test/whatsapp-demo";

/* ✅ ONLY CHANGE: localhost → server */
const MEDIA_UPLOAD_URL = "https://kataria.easivio.io/media/upload";


function CampaignPage() {
  const fileInputRef = useRef(null);

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filters, setFilters] = useState({});

  const [sendText, setSendText] = useState(false);
  const [sendImage, setSendImage] = useState(false);
  const [sendPdf, setSendPdf] = useState(false);

  const [textMessage, setTextMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const { logout } = useAuth();


  /* =====================
     Upload Excel
     ===================== */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (json.length > 0) {
        setColumns(Object.keys(json[0]));
        setRows(json);
        setFilteredRows([]);
        setFilters({});
      }
    };
    reader.readAsBinaryString(file);
  };

  /* =====================
     Filters
     ===================== */
  const toggleFilterValue = (col, value) => {
    setFilters((prev) => {
      const current = prev[col] || [];
      return {
        ...prev,
        [col]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const applyFilters = () => {
    let temp = [...rows];

    Object.keys(filters).forEach((col) => {
      const selected = filters[col];
      if (!selected || selected.length === 0) return;

      temp = temp.filter((row) =>
        selected.includes(String(row[col]))
      );
    });

    setFilteredRows(temp);
  };

  const resetFilters = () => {
    setFilters({});
    setFilteredRows([]);
  };

  const getUniqueValues = (col) =>
    [...new Set(rows.map((r) => r[col]).filter((v) => v !== ""))];

  /* =====================
     Media Upload
     ===================== */
 const uploadFile = async (file, type) => {
  if (!file) return;

  const formData = new FormData();
  formData.append("file", file);

  if (type === "image") setUploadingImage(true);
  if (type === "pdf") setUploadingPdf(true);

  try {
    const res = await fetch(MEDIA_UPLOAD_URL, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Upload failed");

    const data = await res.json();
    if (!data.success || !data.url) throw new Error("Invalid response");

    if (type === "image") setImageUrl(data.url);
    if (type === "pdf") setPdfUrl(data.url);

  } catch (err) {
    console.error(err);
    alert("❌ Upload failed. Media server not reachable.");
  } finally {
    setUploadingImage(false);
    setUploadingPdf(false);
  }
};


  /* =====================
     Send Campaign
     ===================== */
  const handleSendCampaign = async () => {
    const sendTypes = [];
    if (sendText) sendTypes.push("text");
    if (sendImage) sendTypes.push("image");
    if (sendPdf) sendTypes.push("pdf");

    if (sendTypes.length === 0) {
      alert("Select at least one message type");
      return;
    }

    if (sendText && !textMessage.trim()) {
      alert("Please type text message");
      return;
    }

    if (sendImage && !imageUrl) {
      alert("Please upload image");
      return;
    }

    if (sendPdf && !pdfUrl) {
      alert("Please upload PDF");
      return;
    }

    const source = filteredRows.length > 0 ? filteredRows : rows;
    if (source.length === 0) {
      alert("No data");
      return;
    }

    const payload = {
      send_types: sendTypes,
      text_message: sendText ? textMessage : null,
      image_url: imageUrl || null,
      pdf_url: pdfUrl || null,
      customers: source.map((row) => ({
        name: row.name || row.Name || "Customer",
        mobile: row.mobile || row.Mobile || row.phone || "",
        data: row,
      })),
    };

    await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    alert(`Campaign prepared for ${source.length} customers`);
  };

  /* =====================
     Reset All
     ===================== */
  const resetAll = () => {
    setColumns([]);
    setRows([]);
    setFilteredRows([]);
    setFilters({});
    setSendText(false);
    setSendImage(false);
    setSendPdf(false);
    setTextMessage("");
    setImageUrl("");
    setPdfUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const dataToShow = filteredRows.length > 0 ? filteredRows : rows;

  return (
    <div style={styles.page}>
      <h1>📢 WhatsApp Campaign Manager</h1>
      <button
  onClick={logout}
  style={{
    position: "fixed",
    top: 20,
    right: 20,
    padding: "10px 16px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 8
  }}
>
  Logout
</button>


      {/* Upload Excel */}
      <div style={styles.card}>
        <h2>1️⃣ Upload Excel</h2>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          onChange={handleFileUpload}
        />
        <p><b>Total Records:</b> {rows.length}</p>
      </div>

      {/* Filters */}
      {rows.length > 0 && (
        <div style={styles.card}>
          <h2>2️⃣ Filters</h2>
          <div style={styles.filterGrid}>
            {columns.map((col) => (
              <div key={col} style={styles.filterBox}>
                <b>{col}</b>
                <div style={styles.checkboxList}>
                  {getUniqueValues(col).map((val) => (
                    <label key={val} style={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={(filters[col] || []).includes(val)}
                        onChange={() => toggleFilterValue(col, val)}
                      />{" "}
                      {val}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button style={styles.primaryBtn} onClick={applyFilters}>Apply Filters</button>
          <button style={styles.secondaryBtn} onClick={resetFilters}>Reset Filters</button>
        </div>
      )}

      {dataToShow.length > 0 && (
        <div style={styles.card}>
          <h2>3️⃣ Filtered Data</h2>
          <p><b>{dataToShow.length}</b> records selected</p>

          <div style={{ maxHeight: 350, overflow: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} style={styles.th}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataToShow.map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c} style={styles.td}>{row[c]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h2>4️⃣ Message Type</h2>
        <div style={styles.msgType}>
          <label><input type="checkbox" checked={sendText} onChange={(e) => setSendText(e.target.checked)} /> Text</label>
          <label><input type="checkbox" checked={sendImage} onChange={(e) => setSendImage(e.target.checked)} /> Image</label>
          <label><input type="checkbox" checked={sendPdf} onChange={(e) => setSendPdf(e.target.checked)} /> PDF</label>
        </div>
      </div>

      {sendText && (
        <div style={styles.card}>
          <h2>✍️ Type Text Message</h2>
          <textarea
            rows={6}
            placeholder="Type WhatsApp message here..."
            value={textMessage}
            onChange={(e) => setTextMessage(e.target.value)}
            style={{ width: "100%", padding: 14, fontSize: 16 }}
          />
        </div>
      )}

      {sendImage && (
        <div style={styles.card}>
          <h2>5️⃣ Upload Image</h2>
          <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files[0], "image")} />
          {uploadingImage && <p>Uploading image...</p>}
          {imageUrl && <p style={{ color: "green" }}>Image uploaded ✔</p>}
        </div>
      )}

      {sendPdf && (
        <div style={styles.card}>
          <h2>6️⃣ Upload PDF</h2>
          <input type="file" accept="application/pdf" onChange={(e) => uploadFile(e.target.files[0], "pdf")} />
          {uploadingPdf && <p>Uploading PDF...</p>}
          {pdfUrl && <p style={{ color: "green" }}>PDF uploaded ✔</p>}
        </div>
      )}

      <div style={styles.card}>
        <button style={styles.sendBtn} onClick={handleSendCampaign}>🚀 Prepare Campaign</button>
        <button style={styles.resetBtn} onClick={resetAll}>🔄 All Reset</button>
      </div>
    </div>
  );
}

/* =====================
   Styles (UNCHANGED)
   ===================== */
const styles = {
  page: { padding: 40, background: "#f1f5f9", fontFamily: "Segoe UI" },
  card: { background: "#fff", padding: 25, borderRadius: 14, marginBottom: 25, boxShadow: "0 8px 20px rgba(0,0,0,0.08)" },
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 },
  filterBox: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 },
  checkboxList: { maxHeight: 160, overflowY: "auto", marginTop: 6 },
  checkboxItem: { display: "block", fontSize: 14, marginBottom: 4 },
  msgType: { display: "flex", gap: 40, fontSize: 18, fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { border: "1px solid #ddd", padding: 8, background: "#f3f4f6" },
  td: { border: "1px solid #ddd", padding: 8 },
  primaryBtn: { background: "#4f46e5", color: "#fff", padding: "10px 22px", borderRadius: 8, border: "none", marginRight: 10 },
  secondaryBtn: { padding: "10px 22px", borderRadius: 8 },
  sendBtn: { background: "#22c55e", color: "#fff", padding: "14px 34px", borderRadius: 12, fontSize: 18, border: "none" },
  resetBtn: { marginLeft: 12, background: "#9ca3af", color: "#fff", padding: "14px 34px", borderRadius: 12, fontSize: 18, border: "none" },
};

export default CampaignPage;
